import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { SimilarPostItem } from './SimilarPostItem';
import { Skeleton } from '@/components/ui/skeleton';

interface SimilarPostsSidebarProps {
  boardId: string;
  title: string;
}

export function SimilarPostsSidebar({ boardId, title }: SimilarPostsSidebarProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['similarPosts', boardId, title],
    queryFn: () => api.posts.similar({ boardId, title, limit: 5 }),
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!boardId,
  });

  const isRecent = data?.type === 'recent';
  const posts = data?.posts || [];

  return (
    <div className="flex flex-col h-full">
      <h3 className="text-sm font-semibold text-foreground mb-3">
        {isLoading ? 'Loading...' : isRecent ? 'Recent Posts' : 'Similar Posts'}
      </h3>

      <div className="space-y-2 flex-1 overflow-y-auto">
        {isLoading ? (
          <>
            <Skeleton className="h-[72px] w-full" />
            <Skeleton className="h-[72px] w-full" />
            <Skeleton className="h-[72px] w-full" />
          </>
        ) : isError ? (
          <p className="text-xs text-muted-foreground">Failed to load posts</p>
        ) : posts.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {isRecent ? 'No posts in this board yet' : 'No similar posts found'}
          </p>
        ) : (
          posts.map((post) => (
            <SimilarPostItem key={post.id} post={post} />
          ))
        )}
      </div>

      {!isLoading && posts.length > 0 && !isRecent && (
        <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
          Check if your idea already exists before posting
        </p>
      )}
    </div>
  );
}
